// @flow
import _ from 'lodash';
import ko from 'rgs-knockout';

import window from 'window';
import {unwrap} from 'app/rgs/utils/func-unwrapper';
import { Router } from 'app/rgs/routing';

import type { MenuItem } from './js/helpers';
import type {Observable} from 'rgs-knockout/types';
import type { MenuItemModel, IMenuConfiguration } from './types.flow';

const REGEX_ON_INTERPOLATE = /{([^:]*):?([^}]*)?}/gi;

const selectUrl = ({Url}: {Url: string}) => Url || '';

// default
class MenuConfiguration implements IMenuConfiguration {
	static init (): MenuConfiguration {
		return new MenuConfiguration();
	}

	_routeChanges: Observable<HashChangeEvent> = ko.observable(); // workaround

	constructor () {
		this._router = new Router().onRouteChange(this._routeChanges);
	}

	hasItems = (items: Array<any>): boolean => Boolean((items || []).length);

	parseUrl = (URLHandlers?: any = {}) => (url: string) => {
		if (!url) {
			return null;
		}

		return (url || '').replace(
			REGEX_ON_INTERPOLATE,
			(string, name, defaultId = '') => {
				const value = (URLHandlers[name] || {}).value;
				// value может быть пустой строкой,
				// значение по умолчанию проставляется только при отсутствии значения в URLHandlers
				return _.isUndefined(value)
					? defaultId
					: value;
			},
		);
	};

	forceRedirect (url: ?string): boolean {
		if (url) {
			window.location = url;
			return true;
		}
		return false;
	}

	getCurrentLocation (): string {
		return window.location.pathname;
	}


	parseAttributes (item: MenuItem) {
		const {Attributes: attrs} = item || {};

		return (unwrap(attrs) || []).reduce((prev, {Key, Value}) => ({
			...prev,
			[Key]: Value,
		}), {});
	}

	_findOneActiveUrlIncludingSubUrl (baseUrl: string, items: *[]) {
		const urls = [baseUrl, ...items.map(selectUrl)];

		return urls.some(url => url && url.includes(
			(this: IMenuConfiguration).getCurrentLocation()
		));
	}

	setActiveClasses (menuItem: MenuItemModel, more: boolean) {
		return {
			'nfd-nav-list-item-active': ko.pureComputed(() => {
				this._routeChanges(); //track

				const url = ko.unwrap(menuItem.Url);
				const nestedItems = ko.unwrap(menuItem.Items);

				return (this._findOneActiveUrlIncludingSubUrl(url, nestedItems) && !more);
			}),
		};
	}

	setArrowClassesIfMay = (item: MenuItem) => {
		const needArrows = this.hasItems(unwrap(item.Items));

		return {
			'nfd-toggle-right': true,
			'nfd-toggle-plain': needArrows,
			'nfd-toggle': needArrows,
			'nfd-toggle-brand-color': true,
		};
	};

	//eslint-disable-next-line no-empty-function, no-unused-vars
	setActiveMenuItemByKey (key: string): void {

	}
}

class PageMenuConfiguration extends MenuConfiguration {
	_activeMenuKey: Observable<string> = ko.observable();

	/* override */ getCurrentLocation (): string {
		const path = window.location.pathname;
		const hash = window.location.hash;

		return `${path}${hash}`;
	}

	setActiveMenuItemByKey (key: string): void {
		this._activeMenuKey(key);
	}

	setActiveClasses (menuItem: MenuItemModel) {
		return {
			'nfd-nav-list-item-active': ko.pureComputed(
				() => ko.unwrap(menuItem.Key) === this._activeMenuKey()
			),
		};
	}
}

class TabbarMenuConfiguration extends MenuConfiguration {
	_activeMenuKey: Observable<string> = ko.observable();

	/* override */
	getCurrentLocation (): string {
		const path = window.location.pathname;
		const hash = window.location.hash;

		return `${path}${hash}`;
	}

	setActiveMenuItemByKey (key: string): void {
		this._activeMenuKey(key);
	}

	/* override */ 
	setActiveClasses (menuItem: MenuItemModel, more: boolean) {
		const isMore = menuItem.Key === 'more';
		if (isMore) {
			menuItem.Icon.attributes.class = menuItem.IsActive
				? menuItem.Icon.attributes.className + "-active nfd-tabbar-active"
				: menuItem.Icon.attributes.className;

			return {
				'nfd-nav-tabbar-active': menuItem.IsActive,
				'more-active': menuItem.IsActive,
			};
		}
		menuItem.Icon.attributes.class = (menuItem.IsActive && !more)
			? menuItem.Icon.attributes.className + " nfd-tabbar-active"
			: menuItem.Icon.attributes.className;

		return {
			'nfd-nav-tabbar-active': menuItem.IsActive && !more,
			'not-more': menuItem.IsActive && !more,
		};
	}
}

export const usingCfgStrategy = {
	createDefaultMenu: () => new MenuConfiguration(),
	createPageMenu: () => new PageMenuConfiguration(),
	createTabbarMenu: () => new TabbarMenuConfiguration(),
};
